import { useEffect } from 'react';
import seo from '../seo-data.json';

type RoutePath = keyof typeof seo.routes;

/** Find-or-create a <head> tag and set its attributes (no duplicates with the prerendered HTML). */
const upsert = (selector: string, tag: 'meta' | 'link', attrs: Record<string, string>) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tag);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

/**
 * Per-route <title>, description, canonical, Open Graph and Twitter tags.
 * Values come from seo-data.json, which scripts/prerender-meta.mjs also uses to
 * bake the same tags into each route's static HTML at build time.
 */
const Seo = ({ route }: { route: RoutePath }) => {
  useEffect(() => {
    const { title, description } = seo.routes[route];
    const url = seo.siteUrl + (route === '/' ? '/' : route);
    const image = seo.siteUrl + seo.ogImage;

    document.title = title;
    upsert('meta[name="description"]', 'meta', { name: 'description', content: description });
    upsert('link[rel="canonical"]', 'link', { rel: 'canonical', href: url });
    upsert('meta[property="og:title"]', 'meta', { property: 'og:title', content: title });
    upsert('meta[property="og:description"]', 'meta', { property: 'og:description', content: description });
    upsert('meta[property="og:url"]', 'meta', { property: 'og:url', content: url });
    upsert('meta[property="og:image"]', 'meta', { property: 'og:image', content: image });
    upsert('meta[name="twitter:title"]', 'meta', { name: 'twitter:title', content: title });
    upsert('meta[name="twitter:description"]', 'meta', { name: 'twitter:description', content: description });
    upsert('meta[name="twitter:image"]', 'meta', { name: 'twitter:image', content: image });
  }, [route]);

  return null;
};

export default Seo;
